import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import numeral from 'numeral';
import Chart from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import { scroller } from 'react-scroll';

import TradesTable from '@buyside/components/presentation/InsiderTradesPage/TradesTable/tradesTable';
import {
  graphqlTimeframe,
  timeframes
} from '@buyside/components/presentation/CompanyShowPage/CompanyBuysideConsensus/companyChartTimeframeHelper';

import { formatDate } from '@buyside/util/dateHelper';

import {
  LargestInsiderTradesQuery,
  useLargestInsiderTradesQuery
} from '@interfaces/graphql-typings';

import styles from './insiderTradesPageStyles.scss';

const tableName = 'largest-trades';

const processData = (
  trades: LargestInsiderTradesQuery['largestInsiderTrades']
): {
  x: string;
  y: number;
  tooltipHtml: string;
  tradeId: string;
}[] =>
  trades.map(trade => {
    const tradeTooltipHtml = renderToString(
      <div className={styles.tradeTooltip}>
        <div className={styles.header}>
          <p>{trade.name}</p>
        </div>
        <div>
          <p>Date:</p>
          <p>{formatDate(trade.date)}</p>
        </div>
        <div>
          <p>Symbol:</p>
          <p>{trade.symbol}</p>
        </div>
        <div>
          <p>Shares:</p>
          <p>{numeral(trade.numShares).format('0,0')}</p>
        </div>
        <div>
          <p>Purchase / Sale:</p>
          <p
            className={classNames(styles.colored, {
              [styles.red]: trade.tradeType === 'Sale'
            })}
          >
            {trade.tradeType}
          </p>
        </div>
      </div>
    );

    return {
      x: trade.symbol,
      y: trade.numShares,
      tooltipHtml: tradeTooltipHtml,
      tradeId: trade.id
    };
  });

const LargestTrades: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState(timeframes[1]);

  const { data, error, loading } = useLargestInsiderTradesQuery({
    variables: { timeframe: graphqlTimeframe(selectedTimeframe) }
  });

  if (error) throw error;

  const largestInsiderTrades = useMemo(() => data?.largestInsiderTrades || [], [data]);

  const processedData = useMemo(() => processData(largestInsiderTrades), [largestInsiderTrades]);

  const { minShares, maxShares } = useMemo(
    () =>
      largestInsiderTrades.length
        ? {
            minShares: largestInsiderTrades[largestInsiderTrades.length - 1].numShares,
            maxShares: largestInsiderTrades[0].numShares
          }
        : { minShares: 0, maxShares: 0 },
    [largestInsiderTrades]
  );

  return (
    <div id="largest-trades">
      <div className={styles.largestTradesHeader}>
        <h3>Largest Recent Insider Trades</h3>
        <div className={styles.timeframes}>
          {timeframes.map(timeframe => (
            <button
              className={classNames({ [styles.active]: timeframe === selectedTimeframe })}
              key={`${timeframe}-timeframe`}
              onClick={(): void => setSelectedTimeframe(timeframe)}
            >
              {timeframe}
            </button>
          ))}
        </div>
      </div>
      <Chart
        options={{
          chart: {
            events: {
              dataPointSelection: (_e, _context, { seriesIndex, dataPointIndex, w }): void => {
                scroller.scrollTo(`${tableName}-table`, {
                  duration: 250,
                  offset: -70,
                  smooth: 'easeInOutQuint',
                  ignoreCancelEvents: true
                });

                setTimeout(() => {
                  const currentData = w.config.series[seriesIndex].data[dataPointIndex];
                  const row = document.getElementById(tableName + '-' + currentData.tradeId);

                  if (row) row.focus();
                  scroller.scrollTo(`${tableName}-${currentData.tradeId}`, {
                    containerId: `${tableName}-table`,
                    duration: 500,
                    offset: -45,
                    smooth: 'easeInOutQuint',
                    ignoreCancelEvents: true
                  });
                }, 300);
              }
            },
            height: 450,
            type: 'treemap',
            toolbar: {
              show: false
            }
          },
          legend: {
            show: false
          },
          noData: {
            text: loading ? 'Loading...' : 'No Results'
          },
          plotOptions: {
            treemap: {
              enableShades: true,
              shadeIntensity: 0.25,
              reverseNegativeShade: true,
              colorScale: {
                ranges: [
                  {
                    from: minShares,
                    to: 0,
                    color: '#d51800'
                  },
                  {
                    from: 0.1,
                    to: maxShares,
                    color: '#5ab457'
                  }
                ]
              }
            }
          },
          tooltip: {
            custom: function({ seriesIndex, dataPointIndex, w }: any): string {
              const currentData = w.config.series[seriesIndex].data[dataPointIndex];
              return currentData.tooltipHtml;
            }
          }
        }}
        series={[{ data: processedData }]}
        height={450}
        type="treemap"
      />
      <TradesTable loading={loading} tableName={tableName} trades={largestInsiderTrades} />
    </div>
  );
};

export default LargestTrades;
